/**
 * Repo layer — the ONLY way screens touch synced tables. The v2.31.0 save
 * rules live here, not in individual screens (plan §2.4):
 *
 *  1. The client mints the uuid at creation; a save is always an upsert on id.
 *  2. Upserts never touch deleted_at. Only restore() clears it; only
 *     trash() sets it. Both go through their own outbox ops.
 *  3. Rows the server reports deleted are dropped locally on pull (the
 *     engine puts the soft-deleted row; live queries filter deleted_at).
 *  4. Timestamps are adopted onto a snapshot matched by id (engine).
 *  5. Autosave (900 ms) has a flush on pagehide / hidden / app inactive
 *     — see Autosaver below.
 */
import type { Table } from 'dexie';
import type { RouteDb } from '../local/db';
import { newId, nowIso } from '../ids';
import type { BaseRow, SyncTable } from '../types';
import type { SyncEngine } from '../sync/engine';

export interface RepoContext {
  db: RouteDb;
  engine: SyncEngine;
  companyId: string;
  userId: string;
}

export class CompanyRepo<T extends BaseRow> {
  constructor(
    protected ctx: RepoContext,
    protected readonly table: SyncTable,
  ) {}

  protected get t(): Table<T, string> {
    return this.ctx.db.table(this.table) as Table<T, string>;
  }

  /** New row skeleton with a client-minted id. Not saved yet. */
  blank(fields: Omit<T, keyof BaseRow>): T {
    const now = nowIso();
    return {
      id: newId(),
      company_id: this.ctx.companyId,
      created_by: this.ctx.userId,
      created_at: now,
      updated_at: now,
      deleted_at: null,
      ...fields,
    } as T;
  }

  async get(id: string): Promise<T | undefined> {
    return this.t.get(id);
  }

  /** Live (not trashed) rows for the team. */
  async listLive(): Promise<T[]> {
    const rows = await this.t.where('company_id').equals(this.ctx.companyId).toArray();
    return rows.filter((r) => !r.deleted_at);
  }

  async listTrash(): Promise<T[]> {
    const rows = await this.t.where('company_id').equals(this.ctx.companyId).toArray();
    return rows.filter((r) => !!r.deleted_at);
  }

  /**
   * Upsert on id. `deleted_at` is preserved from the local row (never set
   * by a save) and `updated_at` is bumped locally so LWW sees this edit as
   * newer than anything pulled before it.
   */
  async save(row: T): Promise<T> {
    const existing = await this.t.get(row.id);
    const next: T = {
      ...row,
      company_id: this.ctx.companyId,
      deleted_at: existing ? existing.deleted_at : null,
      updated_at: nowIso(),
    };
    await this.t.put(next);
    await this.ctx.engine.enqueue(this.table, 'upsert', next as unknown as { id: string } & Record<string, unknown>);
    return next;
  }

  /** Soft delete. */
  async trash(id: string): Promise<void> {
    const row = await this.t.get(id);
    if (!row) return;
    const next = { ...row, deleted_at: nowIso(), updated_at: nowIso() };
    await this.t.put(next);
    await this.ctx.engine.enqueue(this.table, 'delete', { id });
  }

  /** The only thing that clears deleted_at. */
  async restore(id: string): Promise<void> {
    const row = await this.t.get(id);
    if (!row) return;
    const next = { ...row, deleted_at: null, updated_at: nowIso() };
    await this.t.put(next);
    await this.ctx.engine.enqueue(this.table, 'restore', { id });
  }
}

/**
 * Debounced autosave with a hard flush. Screens call `schedule(fn)` on every
 * keystroke; the fn runs once 900 ms after the last call, or immediately on
 * `flush()` — which the app wires to pagehide, visibilitychange→hidden and
 * Capacitor appStateChange→inactive.
 */
export class Autosaver {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: (() => Promise<void> | void) | null = null;
  constructor(private readonly delayMs = 900) {}

  schedule(fn: () => Promise<void> | void): void {
    this.pending = fn;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.delayMs);
  }

  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const fn = this.pending;
    this.pending = null;
    if (fn) await fn();
  }

  /** Drop the pending save without running it (e.g. the item was trashed). */
  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.pending = null;
  }

  get isPending(): boolean {
    return this.pending !== null;
  }
}
